/**
 * useSpaceDocument - open one real-time space document for a component.
 *
 * Builds a `SpaceDocumentProvider` over the primary environment's RPC
 * session, with an IndexedDB offline copy when the browser has IndexedDB,
 * and destroys it on unmount or when the document changes.
 *
 * `doc` and `awareness` exist as soon as the provider does (an offline copy
 * may already fill the document); `access` stays null until the first sync.
 * An editor should wait for `status === "synced"` the first time and be
 * read-only unless `access === "edit"`. Tiptap's Collaboration extension
 * takes `doc`; CollaborationCaret takes `provider` (it reads
 * `provider.awareness`). Each awareness state carries `user`
 * (`{ id, name, color }`), written by the server.
 *
 * @module spaces/realtime/useSpaceDocument
 */
import { useAtomValue } from "@effect/atom-react";
import type { SpaceDocumentAccess } from "@amoreg/contracts";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { IndexeddbPersistence } from "y-indexeddb";
import type { Awareness } from "y-protocols/awareness";
import type * as Y from "yjs";

import { primaryEnvironmentIdAtom } from "../../state/primaryEnvironment";
import {
  SpaceDocumentProvider,
  type SpaceDocumentPersistenceFactory,
  type SpaceDocumentStatus,
} from "./SpaceDocumentProvider";
import { makeEnvironmentSpaceDocumentTransport } from "./transport";

export interface UseSpaceDocumentResult {
  readonly doc: Y.Doc | null;
  readonly awareness: Awareness | null;
  readonly provider: SpaceDocumentProvider | null;
  readonly status: SpaceDocumentStatus;
  readonly access: SpaceDocumentAccess | null;
}

/** The browser's offline copy; none where IndexedDB is missing (tests, SSR). */
const indexeddbPersistence: SpaceDocumentPersistenceFactory = (name, doc) =>
  typeof indexedDB === "undefined" ? null : new IndexeddbPersistence(name, doc);

const NO_SUBSCRIPTION = () => () => undefined;

/** The providers open on this page, by document, for actions beside the editor. */
const openProviders = new Map<string, SpaceDocumentProvider>();
const providerKey = (spaceId: string, docId: string) => `${spaceId}\u0000${docId}`;

/**
 * The provider of a document open on this page, or null. Save version
 * reads it to wait until the server holds every visible edit.
 */
export function openSpaceDocumentProvider(spaceId: string, docId: string): SpaceDocumentProvider | null {
  return openProviders.get(providerKey(spaceId, docId)) ?? null;
}

export function useSpaceDocument(spaceId: string, docId: string): UseSpaceDocumentResult {
  const environmentId = useAtomValue(primaryEnvironmentIdAtom);
  const [provider, setProvider] = useState<SpaceDocumentProvider | null>(null);

  useEffect(() => {
    if (!environmentId || !spaceId || !docId) return;
    const next = new SpaceDocumentProvider({
      spaceId,
      docId,
      transport: makeEnvironmentSpaceDocumentTransport(environmentId),
      persistence: indexeddbPersistence,
    });
    const key = providerKey(spaceId, docId);
    openProviders.set(key, next);
    // oxlint-disable-next-line react/set-state-in-effect -- Publish the provider only once its cleanup is registered for this document.
    setProvider(next);
    return () => {
      if (openProviders.get(key) === next) openProviders.delete(key);
      next.destroy();
      setProvider((current) => (current === next ? null : current));
    };
  }, [environmentId, spaceId, docId]);

  const subscribeStatus = useCallback(
    (onChange: () => void) => (provider ? provider.on("status", onChange) : NO_SUBSCRIPTION()),
    [provider],
  );
  const subscribeAccess = useCallback(
    (onChange: () => void) => (provider ? provider.on("access", onChange) : NO_SUBSCRIPTION()),
    [provider],
  );
  const status = useSyncExternalStore(subscribeStatus, () => provider?.status ?? "connecting");
  const access = useSyncExternalStore(subscribeAccess, () => provider?.access ?? null);

  return {
    doc: provider?.doc ?? null,
    awareness: provider?.awareness ?? null,
    provider,
    status,
    access,
  };
}
